import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, keepPreviousData, useQueryClient } from '@tanstack/react-query';
import Spring from '@/components/Spring';
import PredictionForm from '@/components/PredictionForm';
import { PredictionCard } from '@/components/PredictionCard';
import { useAuth } from '@/hooks/useAuth';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import { apiClient } from '@/services/api';
import styles from './styles.module.scss';

interface NextMatchData {
  id: number;
  matchDate: string;
  status: string;
  matchday: number;
  homeTeamName: string;
  homeTeamLogo: string | null;
  awayTeamName: string;
  awayTeamLogo: string | null;
  leagueName: string;
  tournamentId: number;
  tournamentName: string;
  manualCoefficients: boolean;
  coefficientsSource?: string;
  team1WinCoefficient?: number | null;
  drawCoefficient?: number | null;
  team2WinCoefficient?: number | null;
  team1Bookmaker?: string | null;
  drawBookmaker?: string | null;
  team2Bookmaker?: string | null;
  prediction: {
    id: number;
    predictedScoreTeam1: number;
    predictedScoreTeam2: number;
    predictedOutcome: string;
    oddsScore: number;
  } | null;
}

const NextMatch: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const isMobile = useBreakpointDown('lg');
  const { t } = useTranslation(['tournament', 'common']);
  const queryClient = useQueryClient();
  const [showPredictionForm, setShowPredictionForm] = useState(false);
  const [skippedIds, setSkippedIds] = useState<number[]>([]);

  const excludeParam = skippedIds.length > 0 ? skippedIds.join(',') : '';

  const { data: matchData, isLoading, isFetching, refetch } = useQuery<NextMatchData | null>({
    queryKey: ['next-to-predict', excludeParam],
    queryFn: async () => {
      const response = await apiClient.getNextToPredict(skippedIds.length > 0 ? skippedIds : undefined);
      return response.data?.data || null;
    },
    enabled: isAuthenticated,
    placeholderData: keepPreviousData,
  });

  const handleSkip = () => {
    if (matchData && !skippedIds.includes(matchData.id)) {
      setSkippedIds(prev => [...prev, matchData.id]);
    }
  };

  const handlePredictionSuccess = useCallback(() => {
    setShowPredictionForm(false);
    refetch();
  }, [refetch]);

  const handleInlineSave = useCallback(async (scoreTeam1: number, scoreTeam2: number, oddsScore?: number) => {
    if (!matchData) return;
    const outcome = scoreTeam1 > scoreTeam2 ? 'team1_win' : scoreTeam1 < scoreTeam2 ? 'team2_win' : 'draw';

    const data: any = {
      match_id: matchData.id,
      tournament_id: matchData.tournamentId,
      predicted_score_team1: scoreTeam1,
      predicted_score_team2: scoreTeam2,
      predicted_outcome: outcome,
    };
    // Only include odds_score when manual coefficients are enabled
    if (matchData.manualCoefficients) {
      data.odds_score = oddsScore;
    }

    if (matchData.prediction) {
      await apiClient.updatePrediction(String(matchData.prediction.id), data);
    } else {
      await apiClient.createPrediction(data);
    }
    // Invalidate the tournament-matches query for this match's tournament
    // so the prediction reflects on the Matches tab when the user
    // navigates there. Centralised invalidation in PredictionForm covers
    // the popup-form path; this inline-save path needs its own call.
    queryClient.invalidateQueries({
      predicate: query => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === 'tournament-matches' &&
          String(key[1]) === String(matchData.tournamentId)
        );
      },
    });
    setShowPredictionForm(false);
    refetch();
  }, [matchData, refetch, queryClient]);

  // Initial loading (no data at all)
  if (isLoading) {
    return (
      <Spring className="card">
        <div className={styles.skeleton}>
          <div className={styles.skeletonHeader}>
            <div className={styles.skeletonBlock} style={{ width: '60px', height: '32px' }} />
            <div className={styles.skeletonBlock} style={{ width: '120px', height: '24px' }} />
          </div>
          <div className={styles.skeletonTeams}>
            <div className={styles.skeletonCircle} />
            <div className={styles.skeletonBlock} style={{ width: '24px', height: '16px' }} />
            <div className={styles.skeletonCircle} />
          </div>
          <div className={styles.skeletonOdds}>
            <div className={styles.skeletonBlock} style={{ flex: 1, height: '48px' }} />
            <div className={styles.skeletonBlock} style={{ flex: 1, height: '48px' }} />
            <div className={styles.skeletonBlock} style={{ flex: 1, height: '48px' }} />
          </div>
        </div>
      </Spring>
    );
  }

  // Empty state
  if (!matchData) {
    if (skippedIds.length > 0) {
      return (
        <Spring className="card">
          <div className={styles.empty}>
            <p className={styles.emptyText}>{t('tournament:noMoreMatchesToPredict')}</p>
          </div>
        </Spring>
      );
    }
    return (
      <Spring className="card">
        <div className={styles.empty}>
          <p className={styles.emptyText} data-testid="next-match-not-participating">{t('tournament:notParticipating')}</p>
        </div>
      </Spring>
    );
  }

  // Mobile: show inline prediction form instead of card
  if (isMobile && showPredictionForm) {
    return (
      <PredictionCard
          predictedScoreTeam1={matchData.prediction?.predictedScoreTeam1}
          predictedScoreTeam2={matchData.prediction?.predictedScoreTeam2}
          predictedOutcome={matchData.prediction?.predictedOutcome}
          scoreCoefficient={matchData.manualCoefficients ? matchData.prediction?.oddsScore : undefined}
          hideOddsInput={!matchData.manualCoefficients}
          matchId={matchData.id}
          tournamentId={matchData.tournamentId}
          canEdit={true}
          initialEditMode={true}
          team1Name={matchData.homeTeamName}
          team2Name={matchData.awayTeamName}
          team1LogoUrl={matchData.homeTeamLogo}
          team2LogoUrl={matchData.awayTeamLogo}
          onEditClick={() => setShowPredictionForm(false)}
          onClose={() => setShowPredictionForm(false)}
          onInlineSave={handleInlineSave}
      />
    );
  }

  // Preview card
  const matchDate = new Date(matchData.matchDate);
  const timeStr = matchDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const isToday = new Date().toDateString() === matchDate.toDateString();
  const isTomorrow = new Date(Date.now() + 86400000).toDateString() === matchDate.toDateString();
  const dateLabel = isToday
    ? t('common:today')
    : isTomorrow
      ? t('tournament:tomorrow')
      : matchDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const hasPrediction = !!matchData.prediction;

  return (
    <>
      {isFetching && createPortal(
        <div className="spinner-overlay"><div className="spinner" /></div>,
        document.body
      )}
      <Spring
          className="card"
          data-testid="next-match-card"
          style={{ opacity: isFetching ? 0.5 : 1, filter: isFetching ? 'blur(2px)' : 'none', transition: 'opacity 0.2s ease, filter 0.2s ease', pointerEvents: isFetching ? 'none' : 'auto' }}
        >
          <div className={styles.main}>
            <div className={styles.header}>
              <div className={styles.time}>
                <h2 className={styles.timeValue}>{timeStr}</h2>
                <span className={styles.timeLabel}>{dateLabel}</span>
              </div>
              <Link to={`/tournaments/${matchData.tournamentId}`} className={styles.tag}>{matchData.tournamentName}</Link>
            </div>

          </div>

          <div className={styles.teams}>
            <div className={styles.team}>
              {matchData.homeTeamLogo && (
                <img src={matchData.homeTeamLogo} alt={matchData.homeTeamName} className={styles.teamLogo} />
              )}
              <span className={styles.teamName}>{matchData.homeTeamName}</span>
            </div>
            <span className={styles.vs}>Vs</span>
            <div className={styles.team}>
              {matchData.awayTeamLogo && (
                <img src={matchData.awayTeamLogo} alt={matchData.awayTeamName} className={styles.teamLogo} />
              )}
              <span className={styles.teamName}>{matchData.awayTeamName}</span>
            </div>
          </div>

          <div className={styles.odds}>
            <div className={styles.oddItem}>
              <span className={styles.oddLabel}>{t('tournament:home1')}</span>
              <span className={styles.oddValue}>{matchData.team1WinCoefficient ?? '—'}</span>
              <span className={styles.oddBookmaker}>{matchData.team1Bookmaker || '\u00A0'}</span>
            </div>
            <div className={styles.oddItem}>
              <span className={styles.oddLabel}>{t('tournament:drawX')}</span>
              <span className={styles.oddValue}>{matchData.drawCoefficient ?? '—'}</span>
              <span className={styles.oddBookmaker}>{matchData.drawBookmaker || '\u00A0'}</span>
            </div>
            <div className={styles.oddItem}>
              <span className={styles.oddLabel}>{t('tournament:away2')}</span>
              <span className={styles.oddValue}>{matchData.team2WinCoefficient ?? '—'}</span>
              <span className={styles.oddBookmaker}>{matchData.team2Bookmaker || '\u00A0'}</span>
            </div>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.predictBtn}
              onClick={() => setShowPredictionForm(true)}
              data-testid="make-prediction-btn"
            >
              {hasPrediction ? t('tournament:editPrediction') : t('tournament:makePrediction')}
            </button>
            <button
              type="button"
              className={styles.skipBtn}
              onClick={handleSkip}
              data-testid="skip-match-btn"
            >
              {t('tournament:skip')}
            </button>
          </div>
      </Spring>

      {/* Desktop: prediction form in popup */}
      {!isMobile && showPredictionForm && (
        <PredictionForm
          match={{
            id: matchData.id,
            tournamentId: matchData.tournamentId,
            homeTeam: matchData.homeTeamName,
            awayTeam: matchData.awayTeamName,
            home_team: matchData.homeTeamName,
            away_team: matchData.awayTeamName,
            scheduledAt: matchData.matchDate,
            status: matchData.status,
            tournament_name: matchData.tournamentName,
            createdAt: '',
            updatedAt: '',
          }}
          tournamentId={matchData.tournamentId}
          hideOddsInput={!matchData.manualCoefficients}
          homeTeamLogo={matchData.homeTeamLogo}
          awayTeamLogo={matchData.awayTeamLogo}
          leagueName={matchData.leagueName}
          prediction={matchData.prediction ? {
            id: matchData.prediction.id,
            userId: 0,
            matchId: matchData.id,
            homeScore: matchData.prediction.predictedScoreTeam1,
            awayScore: matchData.prediction.predictedScoreTeam2,
            predicted_score_team1: matchData.prediction.predictedScoreTeam1,
            predicted_score_team2: matchData.prediction.predictedScoreTeam2,
            predicted_outcome: matchData.prediction.predictedOutcome,
            odds_score: matchData.prediction.oddsScore,
            createdAt: '',
            updatedAt: '',
          } : undefined}
          onClose={() => setShowPredictionForm(false)}
          onSuccess={handlePredictionSuccess}
        />
      )}
    </>
  );
};

export default NextMatch;
